import client from '../../store/axiosApi';
import {
    EduAccess, EduActivity, EduActivityInput, EduAdminDiscipline, EduAttempt, EduDiscipline,
    EduDisciplineInput, EduFile, EduModule, EduOffer, EduPage, EduParticipation, EduQuestion,
    EduQuestionInput, EduSection, EduSectionInput, EduSubmission, EduPost,
    EduCapabilities, EduMode, EduPilotAudit, EduPilotConfiguration, EduPilotGrant, EduPilotGrantInput, EduPilotUser,
} from './types';
import { EDU_MODES, EDU_STAFF_PROFILES, normalizeEduCapabilities, requireEduRevision } from './capabilitiesModel';
import { notifyEduAccessChange } from './accessEvents';

const BASE = '/FermiEdu';
client.interceptors?.response?.use((response: any) => response, (error: any) => {
    const path = error?.config?.url || '';
    if (path.startsWith(BASE + '/') && !path.endsWith('/me/capabilities') && [401, 403, 404].includes(error?.response?.status))
        notifyEduAccessChange({ message: eduErrorMessage(error), token: error?.config?.headers?.Authorization });
    return Promise.reject(error);
});
export type EduListQuery = { search?: string; limit?: number; offset?: number };
export type EduAccessQuery = { limit?: number; offset?: number; from?: string; to?: string };

export function requireEduId(id: number): number {
    if (!Number.isSafeInteger(id) || id < 1) throw new Error('Identificador Fermi Edu inválido.');
    return id;
}

export function eduErrorMessage(error: unknown): string {
    const value = error as { response?: { status?: number; data?: { error?: { message?: string; code?: string } } }; message?: string };
    const status = value?.response?.status;
    const code = value?.response?.data?.error?.code || value?.response?.data?.error?.message;
    const accessErrors: Record<string, string> = {
        EDU_DISABLED: 'O Fermi Edu está desabilitado no momento.',
        EDU_PILOT_REQUIRED: 'Esta conta ou Pessoa não participa do piloto Fermi Edu.',
        EDU_PERMISSION_REQUIRED: 'Sua conta não tem a permissão Fermi Edu necessária para esta ação.',
        EDU_REVISION_CONFLICT: 'As permissões foram alteradas por outra operação. Atualize os dados antes de salvar novamente.',
    };
    if (code && accessErrors[code]) return accessErrors[code];
    if (status === 401) return 'Sua sessão expirou. Entre novamente para continuar.';
    if (status === 403) return 'Você não tem autorização para esta operação Fermi Edu.';
    if (status === 404) return 'O conteúdo não está disponível neste contexto.';
    if (status === 409) return 'A operação foi alterada durante um reenvio. Atualize os dados antes de tentar novamente.';
    if (status === 413) return 'O arquivo excede o limite permitido.';
    const message = value?.response?.data?.error?.message || value?.message || '';
    const descriptions: Record<string, string> = {
        EDU_CURATION_REQUIRED: 'Confirme a equivalência pedagógica e registre uma nota de curadoria antes de publicar.',
        EDU_PUBLICATION_BLOCKED: 'A publicação foi bloqueada. Confira as pendências e as referências do conteúdo.',
        EDU_NO_CORRECT_ANSWER: 'Defina uma resposta correta para a correção automática.',
        EDU_NO_FULL_CREDIT_ANSWER: 'Defina uma resposta que permita obter a pontuação integral.',
        EDU_EMPTY_RANDOM_POOL: 'Selecione questões válidas para o conjunto aleatório.',
        EDU_MATERIAL_REQUIRED: 'Adicione um material didático válido antes de publicar.',
        EDU_FILE_TYPE_NOT_ALLOWED: 'Este tipo de arquivo não é permitido.',
        EDU_INVALID_SCORE: 'Informe uma pontuação válida dentro do limite da atividade.',
        EDU_INVALID_URL: 'Informe um endereço completo que comece com http:// ou https://.',
        EDU_STAFF_POST_FILES_UNSUPPORTED: 'Anexos da equipe devem ser adicionados como materiais de referência na atividade.',
    };
    if (descriptions[message]) return descriptions[message];
    if (/^EDU_[A-Z_]+$/.test(message)) return 'Confira os campos e as regras do conteúdo. A operação não foi concluída.';
    return message || 'Não foi possível concluir a operação Fermi Edu.';
}

export async function getEduCapabilities(signal?: AbortSignal): Promise<EduCapabilities> {
    const response = await client.get<unknown>(`${BASE}/me/capabilities`, {
        headers: { 'Cache-Control': 'no-store' }, ...(signal ? { signal } : {}),
    });
    return normalizeEduCapabilities(response.data);
}
export async function getEduPilot(signal?: AbortSignal): Promise<EduPilotConfiguration> {
    return (await client.get<EduPilotConfiguration>(`${BASE}/admin/pilot`, { headers: { 'Cache-Control': 'no-store' }, ...(signal ? { signal } : {}) })).data;
}
export async function saveEduPilot(mode: EduMode, expectedRevision: number): Promise<EduPilotConfiguration> {
    if (!EDU_MODES.includes(mode)) throw new Error('Selecione um modo Fermi Edu válido.');
    return (await client.put<EduPilotConfiguration>(`${BASE}/admin/pilot`, { mode, expectedRevision: requireEduRevision(expectedRevision) })).data;
}
export async function listEduPilotUsers(query: EduListQuery = {}): Promise<EduPage<EduPilotUser>> {
    return (await client.get<EduPage<EduPilotUser>>(`${BASE}/admin/pilot/users`, { params: query, headers: { 'Cache-Control': 'no-store' } })).data;
}
export async function saveEduPilotGrant(userId: number, input: EduPilotGrantInput): Promise<EduPilotGrant> {
    if (!EDU_STAFF_PROFILES.includes(input.staffProfile) || typeof input.active !== 'boolean' || typeof input.manageAccess !== 'boolean')
        throw new Error('Confira o perfil e a habilitação Fermi Edu selecionados.');
    const body = { active: input.active, staffProfile: input.staffProfile, manageAccess: input.manageAccess,
        studentPersonIds: Array.from(new Set(input.studentPersonIds.map(requireEduId))), expectedRevision: requireEduRevision(input.expectedRevision) };
    return (await client.put<EduPilotGrant>(`${BASE}/admin/pilot/users/${requireEduId(userId)}`, body)).data;
}
export async function listEduPilotAudit(query: Pick<EduListQuery, 'limit' | 'offset'> = {}): Promise<EduPage<EduPilotAudit>> {
    return (await client.get<EduPage<EduPilotAudit>>(`${BASE}/admin/pilot/audit`, { params: query, headers: { 'Cache-Control': 'no-store' } })).data;
}

export async function listEduDisciplines(query: EduListQuery = {}): Promise<EduPage<EduDiscipline>> {
    return (await client.get<EduPage<EduDiscipline>>(`${BASE}/admin/disciplines`, { params: query })).data;
}
export async function getEduDiscipline(id: number): Promise<EduAdminDiscipline> {
    return (await client.get<EduAdminDiscipline>(`${BASE}/admin/disciplines/${requireEduId(id)}`)).data;
}
export async function saveEduDiscipline(input: EduDisciplineInput, id?: number): Promise<EduDiscipline> {
    const body = { name: input.name, descriptionHtml: input.descriptionHtml || '', published: input.published, ...(input.curationNote?.trim() ? { curationNote: input.curationNote.trim() } : {}) };
    return id ? (await client.patch<EduDiscipline>(`${BASE}/admin/disciplines/${requireEduId(id)}`, body)).data
        : (await client.post<EduDiscipline>(`${BASE}/admin/disciplines`, body)).data;
}
export async function saveEduSection(input: EduSectionInput, id?: number): Promise<EduSection> {
    const body = { disciplineId: requireEduId(input.disciplineId), title: input.title,
        descriptionHtml: input.descriptionHtml, position: input.position, published: input.published, ...(input.curationNote?.trim() ? { curationNote: input.curationNote.trim() } : {}) };
    return id ? (await client.patch<EduSection>(`${BASE}/admin/sections/${requireEduId(id)}`, body)).data
        : (await client.post<EduSection>(`${BASE}/admin/sections`, body)).data;
}
export async function saveEduActivity(input: EduActivityInput, id?: number): Promise<EduActivity> {
    const body = { disciplineId: requireEduId(input.disciplineId), sectionId: input.sectionId,
        title: input.title, type: input.type, position: input.position, bodyHtml: input.bodyHtml,
        published: input.published, config: input.config, fileIds: input.fileIds.map(requireEduId), ...(input.curationNote?.trim() ? { curationNote: input.curationNote.trim() } : {}) };
    return id ? (await client.patch<EduActivity>(`${BASE}/admin/activities/${requireEduId(id)}`, body)).data
        : (await client.post<EduActivity>(`${BASE}/admin/activities`, body)).data;
}
export async function listEduQuestions(disciplineId: number, query: EduListQuery = {}): Promise<EduPage<EduQuestion>> {
    return (await client.get<EduPage<EduQuestion>>(`${BASE}/admin/questions`, { params: { ...query, disciplineId: requireEduId(disciplineId) } })).data;
}
export async function saveEduQuestion(input: EduQuestionInput, id?: number): Promise<EduQuestion> {
    const body = { disciplineId: requireEduId(input.disciplineId), type: input.type,
        promptHtml: input.promptHtml, gradingMode: input.gradingMode, definition: input.definition, active: input.active !== false, ...(input.curationNote?.trim() ? { curationNote: input.curationNote.trim() } : {}) };
    return id ? (await client.patch<EduQuestion>(`${BASE}/admin/questions/${requireEduId(id)}`, body)).data
        : (await client.post<EduQuestion>(`${BASE}/admin/questions`, body)).data;
}
export async function listEduModules(query: EduListQuery = {}): Promise<EduPage<EduModule>> {
    return (await client.get<EduPage<EduModule>>(`${BASE}/admin/modules`, { params: query })).data;
}
export async function mapEduModule(moduleId: number, disciplineId: number | null): Promise<EduModule> {
    return (await client.put<EduModule>(`${BASE}/admin/modules/${requireEduId(moduleId)}/discipline`,
        { disciplineId: disciplineId === null ? null : requireEduId(disciplineId) })).data;
}
export async function listEduAccess(disciplineEntreeId: number, query: EduAccessQuery = {}): Promise<EduPage<EduAccess>> {
    return (await client.get<EduPage<EduAccess>>(`${BASE}/admin/entrees/${requireEduId(disciplineEntreeId)}/access`, { params: query })).data;
}
export async function listEduParticipations(activityId: number, disciplineOfferId: number, query: EduListQuery = {}): Promise<EduPage<EduParticipation>> {
    return (await client.get<EduPage<EduParticipation>>(`${BASE}/admin/activities/${requireEduId(activityId)}/participations`,
        { params: { ...query, disciplineOfferId: requireEduId(disciplineOfferId) } })).data;
}
export async function listEduOffers(activityId: number, query: EduListQuery = {}): Promise<EduPage<EduOffer>> {
    return (await client.get<EduPage<EduOffer>>(`${BASE}/admin/activities/${requireEduId(activityId)}/offers`, { params: query })).data;
}
export async function reviewEduSubmission(id: number, score: number | null, feedbackHtml: string): Promise<EduSubmission> {
    return (await client.post<EduSubmission>(`${BASE}/admin/submissions/${requireEduId(id)}/review`, { score, feedbackHtml })).data;
}
export async function reviewEduAttempt(id: number, reviews: { slotId: string; score: number; feedbackHtml: string }[]): Promise<EduAttempt> {
    return (await client.post<EduAttempt>(`${BASE}/admin/attempts/${requireEduId(id)}/review`, { reviews })).data;
}
export async function uploadEduFile(file: File, disciplineId: number, visibility: EduFile['visibility'], activityId?: number): Promise<EduFile> {
    return (await client.post<EduFile>(`${BASE}/admin/files`, file, {
        headers: { 'Content-Type': 'application/octet-stream' },
        params: { name: file.name, mimeType: file.type || 'application/octet-stream',
            disciplineId: requireEduId(disciplineId), visibility, ...(activityId ? { activityId: requireEduId(activityId) } : {}) },
    })).data;
}
export async function getEduFileBlob(fileId: number, context: { disciplineEntreeId?: number; disciplineOfferId?: number; activityId?: number } = {}, signal?: AbortSignal): Promise<Blob> {
    const params = { ...(context.disciplineEntreeId ? { disciplineEntreeId: requireEduId(context.disciplineEntreeId) } : {}),
        ...(context.disciplineOfferId ? { disciplineOfferId: requireEduId(context.disciplineOfferId) } : {}),
        ...(context.activityId ? { activityId: requireEduId(context.activityId) } : {}) };
    return (await client.get<Blob>(`${BASE}/files/${requireEduId(fileId)}/content`, { responseType: 'blob', params, ...(signal ? { signal } : {}) })).data;
}
export async function postEduStaffForum(activityId: number, disciplineOfferId: number, text: string, clientRequestId: string, parentId?: number): Promise<EduPost> {
    return (await client.post<EduPost>(`${BASE}/admin/activities/${requireEduId(activityId)}/posts`, {
        disciplineOfferId: requireEduId(disciplineOfferId), text, clientRequestId,
        ...(parentId ? { parentId: requireEduId(parentId) } : {}), fileIds: [],
    })).data;
}
export async function updateEduFile(fileId: number, visibility: EduFile['visibility'], curationNote?: string): Promise<EduFile> {
    return (await client.patch<EduFile>(`${BASE}/admin/files/${requireEduId(fileId)}`, { visibility,
        ...(curationNote?.trim() ? { curationNote: curationNote.trim() } : {}) })).data;
}
